/**
 * NearestLakes — Pro/Elite-only "lakes within X miles of you" card.
 *
 * Reads the saved weather location from localStorage (cc_weather_location),
 * falls back to the live resolver if nothing is saved, and lets the user
 * flip between 10 / 25 / 50 mi radii. Each tap re-fetches against the
 * backend (which haversine-filters lake docs that have lat/lng).
 *
 * Why a separate component instead of folding into SmartRecommendations?
 *   - it has its own loading + permission states (Pro gate, no saved
 *     location, no lakes within radius)
 *   - it owns its own "change location" affordance that opens the
 *     WeatherWidget settings paradigm (we re-use the same lib)
 *   - it can be dropped anywhere on the dashboard without coupling
 */
import { useEffect, useMemo, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MapPin, Locate, Search, ChevronRight, Loader2, Lock, Sparkles,
  X, RefreshCw,
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { useAuth } from '../context/AuthContext';
import {
  getSavedLocation, saveLocation, resolveLocation,
  requestBrowserGeolocation, reverseGeocode, searchPlaces,
} from '../lib/weatherLocation';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RADII = [10, 25, 50];

const isPro = (u) => ['pro', 'elite'].includes((u?.subscription_tier || '').toLowerCase()) || u?.is_admin;

export default function NearestLakes() {
  const { user, token } = useAuth();
  const [location, setLocation] = useState(() => getSavedLocation());
  const [radius, setRadius] = useState(25);
  const [lakes, setLakes] = useState([]);
  const [totalWithinRadius, setTotalWithinRadius] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showLocPicker, setShowLocPicker] = useState(false);
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [resolving, setResolving] = useState(false);

  const pro = useMemo(() => isPro(user), [user]);

  // If we don't have a saved location yet, resolve once (GPS/IP fallback)
  // so the user sees something the moment they land on the Dashboard.
  useEffect(() => {
    if (location) return;
    let cancelled = false;
    (async () => {
      setResolving(true);
      const loc = await resolveLocation({ preferGps: false });
      if (!cancelled) setLocation(loc);
      setResolving(false);
    })();
    return () => { cancelled = true; };
  }, [location]);

  const fetchLakes = useCallback(async () => {
    if (!pro || !location?.latitude || !location?.longitude || !token) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/recommendations/nearest-lakes`, {
        params: { lat: location.latitude, lng: location.longitude, radius_mi: radius, limit: 12 },
        headers: { Authorization: `Bearer ${token}` },
      });
      setLakes(r.data.lakes || []);
      setTotalWithinRadius(r.data.total_within_radius || 0);
    } catch (err) {
      if (err.response?.status === 402) {
        // Stale tier in token — pro view but server says no.
        toast.error('Pro membership required for nearest-lakes search.');
      } else {
        toast.error('Could not load nearby lakes');
      }
    } finally {
      setLoading(false);
    }
  }, [location, radius, token, pro]);

  useEffect(() => { fetchLakes(); }, [fetchLakes]);

  // ---- location picker handlers ----
  const useMyLocation = async () => {
    try {
      const { latitude, longitude } = await requestBrowserGeolocation();
      const place = await reverseGeocode(latitude, longitude);
      const loc = { latitude, longitude, ...place, source: 'gps' };
      setLocation(loc);
      saveLocation(loc);
      setShowLocPicker(false);
      toast.success(`Using ${place.name || 'your location'}`);
    } catch (err) {
      toast.error(err?.code === 1 ? 'Location permission denied' : 'Could not get your location');
    }
  };

  const onSearchChange = async (val) => {
    setQuery(val);
    if (val.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const rows = await searchPlaces(val);
    setSearchResults(rows);
  };

  const pickPlace = (place) => {
    const loc = { ...place, source: 'manual' };
    setLocation(loc);
    saveLocation(loc);
    setShowLocPicker(false);
    setQuery('');
    setSearchResults([]);
    toast.success(`Saved ${place.name} as your location`);
  };

  // ---- gating ----
  if (!pro) {
    return (
      <div className="glass-card p-6 mb-8 relative overflow-hidden" data-testid="nearest-lakes-locked">
        <div className="absolute -top-12 -right-12 w-40 h-40 bg-[#c8b273]/10 rounded-full blur-3xl pointer-events-none" />
        <div className="flex items-start gap-3">
          <Lock className="w-5 h-5 text-[#c8b273] mt-0.5" />
          <div className="flex-1">
            <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold mb-1">
              Pro feature
            </p>
            <h3 className="font-['Barlow_Condensed'] text-2xl font-bold uppercase tracking-wide text-white">
              Find lakes near you
            </h3>
            <p className="text-[#8a948a] text-sm mt-2 max-w-md">
              Upgrade to Pro and we'll surface every venue within 10, 25 or 50 miles of your
              saved location — so the right water is always one tap away.
            </p>
            <Link to="/subscription">
              <Button className="btn-gold mt-4" data-testid="nearest-lakes-upgrade-btn">
                <Sparkles className="w-4 h-4 mr-1" /> Upgrade to Pro
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="glass-card p-6 mb-8 relative overflow-hidden" data-testid="nearest-lakes-card">
      <div className="absolute -top-12 -left-12 w-40 h-40 bg-[#c8b273]/10 rounded-full blur-3xl pointer-events-none" />

      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-start gap-3 min-w-0">
          <MapPin className="w-6 h-6 text-[#c8b273] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-wider text-[#c8b273] font-bold">Pro · Nearest lakes</p>
            <h2 className="font-['Barlow_Condensed'] text-2xl font-extrabold uppercase tracking-wide text-white">
              Closest waters to you
            </h2>
            <p className="text-[#8a948a] text-xs mt-0.5 truncate" data-testid="nearest-lakes-origin">
              {resolving ? 'Resolving location…' : location?.name
                ? <>From <span className="text-white font-bold">{location.name}</span>{location.region ? `, ${location.region}` : ''}</>
                : 'No location set'}
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowLocPicker((v) => !v)}
          className="text-xs px-3 py-1.5 rounded border border-[#c8b273]/30 text-[#c8b273] hover:bg-[#c8b273]/10 transition-colors"
          data-testid="nearest-lakes-change-location"
        >
          {showLocPicker ? <X className="w-3.5 h-3.5 inline -mt-0.5 mr-1" /> : <MapPin className="w-3.5 h-3.5 inline -mt-0.5 mr-1" />}
          {showLocPicker ? 'Close' : 'Change location'}
        </button>
      </div>

      {/* Location picker panel */}
      {showLocPicker && (
        <div className="mb-4 p-3 bg-[#0a0c0a] border border-white/5 rounded-lg" data-testid="nearest-lakes-loc-panel">
          <div className="flex flex-wrap gap-2 mb-3">
            <Button onClick={useMyLocation} className="btn-gold flex-1" size="sm" data-testid="nearest-lakes-use-my-location">
              <Locate className="w-4 h-4 mr-2" /> Use my location
            </Button>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#6b7d6b]" />
            <Input
              value={query}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search town, city or postcode area..."
              className="pl-9"
              data-testid="nearest-lakes-search-input"
            />
          </div>
          {searchResults.length > 0 && (
            <div className="mt-2 max-h-48 overflow-y-auto rounded border border-[#1f221f] bg-[#0b0d0b]">
              {searchResults.map((p) => (
                <button
                  key={p.id}
                  onClick={() => pickPlace(p)}
                  className="w-full text-left px-3 py-2 hover:bg-[#141714] text-sm text-white border-b border-[#141714] last:border-0"
                  data-testid={`nearest-lakes-result-${p.id}`}
                >
                  <span className="font-bold">{p.name}</span>
                  {(p.region || p.country) && (
                    <span className="text-[#8a948a] text-xs ml-2">{[p.region, p.country].filter(Boolean).join(', ')}</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Radius selector */}
      <div className="flex items-center gap-2 mb-4" role="tablist" aria-label="Search radius">
        {RADII.map((r) => (
          <button
            key={r}
            role="tab"
            aria-selected={radius === r}
            onClick={() => setRadius(r)}
            className={`flex-1 py-2 rounded text-sm font-bold uppercase tracking-wider transition-colors border ${
              radius === r
                ? 'bg-[#c8b273] text-black border-[#c8b273]'
                : 'bg-[#141714] text-[#c0c4c0] border-[#1f221f] hover:border-[#c8b273]/40'
            }`}
            data-testid={`nearest-lakes-radius-${r}`}
          >
            Within {r} mi
          </button>
        ))}
        <button
          onClick={fetchLakes}
          className="p-2 text-[#8a948a] hover:text-[#c8b273]"
          data-testid="nearest-lakes-refresh"
          aria-label="Refresh"
        >
          <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
        </button>
      </div>

      {/* Results */}
      {loading ? (
        <div className="flex items-center justify-center py-10" data-testid="nearest-lakes-loading">
          <Loader2 className="w-6 h-6 animate-spin text-[#c8b273]" />
        </div>
      ) : lakes.length === 0 ? (
        <div className="text-center py-8" data-testid="nearest-lakes-empty">
          <MapPin className="w-10 h-10 text-[#4a504a] mx-auto mb-2" />
          <p className="text-white font-semibold">No lakes within {radius} miles</p>
          <p className="text-[#8a948a] text-sm mt-1">
            Try a wider radius{location?.name ? ` from ${location.name}` : ''}, or pick a different location.
          </p>
        </div>
      ) : (
        <>
          <p className="text-[10px] uppercase tracking-wider text-[#8a948a] mb-2" data-testid="nearest-lakes-count">
            Showing {lakes.length} of {totalWithinRadius} within {radius} mi
          </p>
          <div className="grid sm:grid-cols-2 gap-3" data-testid="nearest-lakes-list">
            {lakes.map((l) => (
              <Link
                key={l.id}
                to={`/lakes/${l.id}`}
                className="group bg-[#141714] border border-[#1f221f] rounded-lg p-3 hover:border-[#c8b273] transition-colors flex items-center gap-3"
                data-testid={`nearest-lake-${l.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-white font-bold truncate">{l.name}</p>
                    <span className="text-[10px] font-bold text-[#c8b273] bg-[#c8b273]/15 border border-[#c8b273]/30 rounded px-1.5 py-0.5 whitespace-nowrap">
                      {l.distance_mi} mi
                    </span>
                  </div>
                  <p className="text-[#8a948a] text-xs truncate">
                    {[l.location, l.county].filter(Boolean).join(' · ') || l.region || '—'}
                  </p>
                  {l.day_ticket_price && (
                    <p className="text-[#c8b273] text-xs mt-1">From £{l.day_ticket_price}/day</p>
                  )}
                </div>
                <ChevronRight className="w-5 h-5 text-[#6b7d6b] group-hover:text-[#c8b273] flex-shrink-0" />
              </Link>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
